import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { toast } from 'sonner';
import { useCelebration } from '@/context/CelebrationContext';
import { 
  Sparkles, Check, Lock, Clock, Diamond, Trophy, 
  ChevronRight, Flame, Heart, Target, Zap, BookOpen, ArrowRight, Star, Users
} from 'lucide-react';

const API_URL = window.location.origin;

const weekIcons = {
  week_1: Flame,    // Truth
  week_2: Heart,    // Support
  week_3: Target,   // Transformation
  week_4: Zap       // Action
};

// Week completion messages - with multiple variations
const WEEK_COMPLETION_MESSAGES = {
  week_1: {
    title: "Week 1 Complete: You've Reclaimed Your Truth!",
    titleVariants: [
      "Week 1 Complete: You've Reclaimed Your Truth! 💎",
      "Week 1 Done: Your Truth is Revealed! ✨",
      "Week 1 Victory: You Know Who You Are Now! 👑"
    ],
    subtitle: "YOUR TRUTH",
    messageVariants: [
      "Queen, you've done something powerful. You've stopped to look at who you really are beneath the expectations, the masks, and the roles others placed on you. This clarity is your foundation.",
      "You've peeled back the layers and discovered your authentic self. This isn't just self-reflection—it's reclamation. Your truth is your power.",
      "Look at you, taking the time to really see yourself. The clarity you've gained this week will guide every decision you make from here."
    ],
    journalPrompts: [
      "What surprised you most about yourself this week? What truth did you uncover that you want to hold onto?",
      "What old story about yourself did you release this week? How does it feel to let it go?",
      "What's one truth about yourself that you're now ready to own fully?"
    ],
    nextWeekPreview: "Next week, you'll anchor into sisterhood and identify where you want to grow.",
    color: "#C45C26"
  },
  week_2: {
    title: "Week 2 Complete: You're No Longer Alone!",
    titleVariants: [
      "Week 2 Complete: You're No Longer Alone! 💜",
      "Week 2 Done: You've Found Your Tribe! 🤝🏾",
      "Week 2 Victory: Sisterhood Activated! ✨"
    ],
    subtitle: "UNWAVERING SUPPORT",
    messageVariants: [
      "Isolation ends here. You've stepped into sisterhood, asked for help, and set a clear intention. You're not doing this journey alone anymore—we've got you.",
      "You've broken through the barrier of going it alone. By reaching out and connecting, you've multiplied your strength. This is what sisterhood looks like.",
      "Asking for help isn't weakness—it's wisdom. This week you chose connection over isolation, and that choice will change everything."
    ],
    journalPrompts: [
      "How did it feel to ask for help? What did you learn about belonging this week?",
      "What surprised you about the support you received this week?",
      "How has connecting with your sisters changed your perspective?"
    ],
    nextWeekPreview: "Next week, you'll turn your intention into strategy with focused, mindful transformation.",
    color: "#2d4a3e"
  },
  week_3: {
    title: "Week 3 Complete: Momentum Is Building!",
    titleVariants: [
      "Week 3 Complete: Momentum Is Building! 🔥",
      "Week 3 Done: You're In The Zone! 💪🏾",
      "Week 3 Victory: Strategy Meets Action! ⚡"
    ],
    subtitle: "MINDFUL TRANSFORMATION",
    messageVariants: [
      "You've moved from dreaming to planning. You've chosen your priority, mapped your moves, and removed distractions. This is what intentional growth looks like.",
      "This week you proved that transformation isn't accidental—it's designed. You've laid the foundation for real, lasting change.",
      "Look at you, getting strategic about your growth! The clarity and focus you've built this week is your launchpad for everything that comes next."
    ],
    journalPrompts: [
      "What did you have to let go of this week to make space for what matters? How does that feel?",
      "What's one habit or routine you established this week that you want to keep?",
      "How has being more intentional changed your daily experience?"
    ],
    nextWeekPreview: "Next week, you'll take bold, visible action and celebrate your wins!",
    color: "#8B7355"
  },
  week_4: {
    title: "Week 4 Complete: You Did It, Queen!",
    titleVariants: [
      "Week 4 Complete: You Did It, Queen! 👑",
      "Week 4 Done: 30 Days of Transformation! 🎉",
      "Week 4 Victory: You've Activated Your Power! 💎"
    ],
    subtitle: "MEANINGFUL ACTION",
    messageVariants: [
      "Look at you! In just 4 weeks, you've reclaimed your truth, found your people, created strategy, and taken bold action. This is just the beginning of your transformation.",
      "30 days ago, you made a choice. Today, you're proof that choice matters. You've done the work, and it shows. This is who you've always been—you just needed to remember.",
      "Queen, you've shown up for yourself every single week. Truth, support, strategy, action—you've built a foundation for the life you deserve. And this is only the beginning."
    ],
    journalPrompts: [
      "What evidence of change do you see in yourself? What's one thing you're most proud of from these 30 days?",
      "Who were you 30 days ago, and who are you now? What's different?",
      "What will you carry forward from this experience? What commitment do you make to yourself going forward?"
    ],
    nextWeekPreview: "Continue your journey with 6-Week Challenges, YUMMY Declaration, and more!",
    color: "#D4AF37"
  }
};

// Helper function to get random variation
const getRandomVariation = (arr) => arr[Math.floor(Math.random() * arr.length)];

const ActivationPathway = () => {
  const navigate = useNavigate();
  const [pathway, setPathway] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTask, setActiveTask] = useState(null);
  const [taskResponse, setTaskResponse] = useState('');
  const [completing, setCompleting] = useState(false);
  const [showDay30Dialog, setShowDay30Dialog] = useState(false);
  const [day30Responses, setDay30Responses] = useState({});
  const [weekCompletionDialog, setWeekCompletionDialog] = useState(null);  // Which week was just completed
  const { celebrate, quickCelebrate, grandCelebrate } = useCelebration();

  useEffect(() => {
    fetchPathway();
  }, []);

  const fetchPathway = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/pathway/progress`);
      setPathway(response.data);
      
      // Auto-initialize if not started
      if (!response.data.initialized) {
        await axios.post(`${API_URL}/api/pathway/initialize`);
        const refreshed = await axios.get(`${API_URL}/api/pathway/progress`);
        setPathway(refreshed.data);
      }
    } catch (error) {
      console.error('Failed to fetch pathway:', error);
      toast.error('Failed to load activation pathway');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteTask = async () => {
    if (!activeTask) return;

    setCompleting(true);
    try {
      const result = await axios.post(`${API_URL}/api/pathway/complete-task`, {
        task_id: activeTask.id,
        response: taskResponse
      });
      
      // Check if the task was locked
      if (result.data.locked) {
        toast.error(result.data.error);
        setActiveTask(null);
        setTaskResponse('');
        return;
      }
      
      // Check if community post is required
      if (result.data.requires_community_post) {
        toast.error(result.data.error, {
          duration: 5000,
          icon: '💜'
        });
        return;
      }
      
      quickCelebrate(`+${result.data.points_earned} diamonds earned! 💎`);
      setActiveTask(null);
      setTaskResponse('');
      
      // Fetch updated pathway to check for week completion
      const updatedPathway = await axios.get(`${API_URL}/api/pathway/progress`);
      setPathway(updatedPathway.data);
      
      // Check if a week was just completed
      const weeks = updatedPathway.data.weeks;
      for (const [weekKey, weekData] of Object.entries(weeks)) {
        // Check if this week is now 100% complete and wasn't before (indicated by having the task we just completed)
        if (weekData.is_complete) {
          const taskInWeek = weekData.tasks.find(t => t.id === activeTask.id);
          if (taskInWeek) {
            // This week was just completed!
            grandCelebrate(`${WEEK_COMPLETION_MESSAGES[weekKey].title}`);
            setTimeout(() => {
              setWeekCompletionDialog(weekKey);
            }, 1500);
            break;
          }
        }
      }
    } catch (error) {
      const errorData = error.response?.data;
      const errorMsg = errorData?.error || 'Failed to complete task';
      
      // Handle community post requirement error with better UX
      if (errorData?.requires_community_post) {
        toast.error(errorMsg, {
          duration: 5000,
          icon: '💜'
        });
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setCompleting(false);
    }
  };

  const handleDay30Submit = async () => {
    setCompleting(true);
    try {
      await axios.post(`${API_URL}/api/pathway/day-30-checkin`, {
        responses: day30Responses
      });
      
      celebrate('grand', 'You completed your 30-Day Activation! Your 90-Day YUMMY Expansion Plan awaits! 🎉');
      setShowDay30Dialog(false);
      fetchPathway();
    } catch (error) {
      toast.error('Failed to complete check-in');
    } finally {
      setCompleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin h-8 w-8 border-4 border-terracotta border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!pathway) return null;

  const { user_progress, weeks, day_30_checkin, total_possible_points } = pathway;
  const progressPercent = user_progress ? (user_progress.total_points_earned / total_possible_points) * 100 : 0;

  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <Card className="text-white shadow-lg" style={{ background: 'linear-gradient(to right, #1A3C34, #065f46, #1A3C34)' }}>
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h2 className="font-heading text-2xl mb-1 text-white drop-shadow-md">30-Day YUMMY Activation</h2>
              <p className="text-white/90">
                {user_progress?.status === 'completed' 
                  ? '🎉 Pathway Completed!' 
                  : `Day ${user_progress?.days_elapsed || 0} of 30`}
              </p>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-center">
                <div className="flex items-center gap-2">
                  <Diamond className="h-5 w-5 text-amber-300" />
                  <span className="text-2xl font-bold">{user_progress?.total_points_earned || 0}</span>
                </div>
                <p className="text-xs text-white/70">Points Earned</p>
              </div>
              <div className="text-center">
                <span className="text-2xl font-bold">{user_progress?.days_remaining || 30}</span>
                <p className="text-xs text-white/70">Days Left</p>
              </div>
            </div>
          </div>
          <div className="mt-4">
            <Progress value={progressPercent} className="h-3 bg-white/20" />
            <p className="text-xs text-white/70 mt-1 text-right">
              {Math.round(progressPercent)}% Complete
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Weekly Accordions */}
      <Accordion type="single" collapsible defaultValue={`week_${user_progress?.current_week || 1}`}>
        {Object.entries(weeks).map(([weekKey, week]) => {
          const WeekIcon = weekIcons[weekKey] || Sparkles;
          const isCurrentWeek = week.number === user_progress?.current_week;
          const isCompleted = week.is_complete || week.progress_percent === 100;
          const isLocked = !week.is_unlocked;

          return (
            <AccordionItem key={weekKey} value={weekKey} disabled={isLocked}>
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-4 w-full">
                  <div 
                    className={`h-12 w-12 rounded-full flex items-center justify-center ${
                      isCompleted ? 'bg-green-500' : isLocked ? 'bg-gray-300' : ''
                    }`}
                    style={{ backgroundColor: !isCompleted && !isLocked ? week.color : undefined }}
                  >
                    {isCompleted ? (
                      <Check className="h-6 w-6 text-white" />
                    ) : isLocked ? (
                      <Lock className="h-5 w-5 text-gray-500" />
                    ) : (
                      <WeekIcon className="h-6 w-6 text-white" />
                    )}
                  </div>
                  <div className="flex-1 text-left">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-heading text-lg">Week {week.number}: {week.theme}</span>
                      {isCurrentWeek && !isLocked && (
                        <Badge className="bg-terracotta text-white text-xs">Current</Badge>
                      )}
                      {isLocked && week.unlock_reason && (
                        <Badge variant="outline" className="text-xs bg-gray-50 text-gray-500">
                          <Clock className="h-3 w-3 mr-1" />
                          {week.days_until_unlock > 0 
                            ? `Day ${week.unlock_day}` 
                            : week.unlock_reason}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">
                      {isLocked 
                        ? week.days_until_unlock > 0 
                          ? `Unlocks in ${week.days_until_unlock} days`
                          : 'Complete previous week to unlock'
                        : `${week.keyword} • ${week.completed_tasks}/${week.total_tasks} tasks`
                      }
                    </p>
                  </div>
                  <div className="hidden md:block">
                    <Progress value={isLocked ? 0 : week.progress_percent} className="w-24 h-2" />
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="pt-4 pb-2 px-2">
                  <p className="text-gray-600 mb-4">{week.description}</p>
                  
                  <div className="grid gap-3">
                    {week.tasks.map((task) => (
                      <div 
                        key={task.id}
                        className={`p-4 rounded-lg border-2 transition-all ${
                          task.completed 
                            ? 'bg-green-50 border-green-200' 
                            : isLocked
                            ? 'bg-gray-50 border-gray-200 opacity-60'
                            : 'bg-white border-gray-200 hover:border-terracotta cursor-pointer'
                        }`}
                        onClick={() => !task.completed && !isLocked && setActiveTask(task)}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                            task.completed ? 'bg-green-500' : 'bg-gray-100'
                          }`}>
                            {task.completed ? (
                              <Check className="h-4 w-4 text-white" />
                            ) : isLocked ? (
                              <Lock className="h-3 w-3 text-gray-400" />
                            ) : (
                              <span className="text-sm text-gray-400">{week.tasks.indexOf(task) + 1}</span>
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <h4 className={`font-medium ${
                                task.completed ? 'text-green-700' : isLocked ? 'text-gray-400' : 'text-forest'
                              }`}>
                                {task.title}
                              </h4>
                              <Badge variant="outline" className={`text-xs ${isLocked ? 'opacity-50' : ''}`}>
                                <Diamond className="h-3 w-3 mr-1" />
                                {task.points}
                              </Badge>
                            </div>
                            <p className={`text-sm mt-1 ${isLocked ? 'text-gray-400' : 'text-gray-500'}`}>
                              {task.description}
                            </p>
                            {task.completed && task.user_response && (
                              <div className="mt-2 p-2 bg-green-100 rounded text-sm text-green-800 italic">
                                "{task.user_response.substring(0, 100)}{task.user_response.length > 100 ? '...' : ''}"
                              </div>
                            )}
                          </div>
                          {!task.completed && !isLocked && (
                            <ChevronRight className="h-5 w-5 text-gray-400" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-4 p-3 bg-amber-50 rounded-lg">
                    <p className="text-sm text-amber-800">
                      <strong>Week {week.number} Outcome:</strong> {week.outcome}
                    </p>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>

      {/* Day 30 Check-In Card */}
      <Card className={`border-2 ${day_30_checkin.completed ? 'border-gold bg-amber-50' : day_30_checkin.unlocked ? 'border-amber-300' : 'border-gray-200'}`}>
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className={`h-14 w-14 rounded-full flex items-center justify-center ${
              day_30_checkin.completed ? 'bg-gradient-to-br from-amber-400 to-yellow-500' : 
              day_30_checkin.unlocked ? 'bg-amber-100' : 'bg-gray-100'
            }`}>
              {day_30_checkin.completed ? (
                <Trophy className="h-7 w-7 text-white" />
              ) : day_30_checkin.unlocked ? (
                <Trophy className="h-7 w-7 text-amber-600" />
              ) : (
                <Lock className="h-6 w-6 text-gray-400" />
              )}
            </div>
            <div className="flex-1">
              <h3 className="font-heading text-xl text-forest">{day_30_checkin.title}</h3>
              <p className="text-sm text-gray-600">{day_30_checkin.description}</p>
              {day_30_checkin.completed && (
                <Badge className="mt-2 bg-gold text-white">
                  <Diamond className="h-3 w-3 mr-1" />
                  +{day_30_checkin.points} points earned
                </Badge>
              )}
            </div>
            {!day_30_checkin.completed && (
              <Button 
                onClick={() => setShowDay30Dialog(true)}
                disabled={!day_30_checkin.unlocked}
                className={day_30_checkin.unlocked ? 'btn-primary' : ''}
              >
                {day_30_checkin.unlocked ? 'Complete Check-In' : 'Unlocks Day 25+'}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Task Completion Dialog */}
      <Dialog open={!!activeTask} onOpenChange={() => setActiveTask(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {activeTask?.title}
              <Badge variant="outline" className="ml-2">
                <Diamond className="h-3 w-3 mr-1" />
                {activeTask?.points} points
              </Badge>
            </DialogTitle>
            <DialogDescription>{activeTask?.description}</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {activeTask?.prompt && (
              <div className="p-4 bg-sand/50 rounded-lg">
                <p className="text-forest font-medium">{activeTask.prompt}</p>
              </div>
            )}
            
            {activeTask?.type === 'reflection' || activeTask?.type === 'action' ? (
              <Textarea
                placeholder="Share your reflection..."
                value={taskResponse}
                onChange={(e) => setTaskResponse(e.target.value)}
                className="min-h-[120px]"
              />
            ) : activeTask?.type === 'community_action' ? (
              <div className="space-y-4">
                <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-3 text-purple-800">
                    <Sparkles className="h-5 w-5" />
                    <span className="font-medium">Community Engagement Task</span>
                  </div>
                  <p className="text-sm text-purple-700 mb-4">
                    Share something meaningful with your sisters in the community. Once you've posted, we'll automatically detect it and mark this task complete!
                  </p>
                  <Button
                    variant="default"
                    onClick={() => navigate('/circles')}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                    data-testid="go-to-circles-btn"
                  >
                    <Users className="h-4 w-4 mr-2" />
                    Go to Community Circles
                    <ChevronRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-xs text-amber-700">
                    <strong>Tip:</strong> After posting in the community, click "Complete Task" below. Your post will be detected automatically. If you've already posted today, just click complete!
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Or paste what you shared (optional):
                  </label>
                  <Textarea
                    placeholder="Paste your community post here if you prefer manual verification..."
                    value={taskResponse}
                    onChange={(e) => setTaskResponse(e.target.value)}
                    className="min-h-[60px]"
                  />
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-500 italic">
                Complete this action, then click below to mark it done.
              </p>
            )}
          </div>

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setActiveTask(null)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCompleteTask}
              disabled={completing || (activeTask?.type !== 'community_action' && !taskResponse.trim())}
              className="btn-primary"
            >
              {completing ? 'Checking...' : 'Complete Task'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Day 30 Check-In Dialog */}
      <Dialog open={showDay30Dialog} onOpenChange={setShowDay30Dialog}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trophy className="h-6 w-6 text-amber-500" />
              Day 30 Legacy Check-In
            </DialogTitle>
            <DialogDescription>
              Reflect on your 30-day transformation and set your vision for the next 90 days.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {day_30_checkin.questions?.map((question, idx) => (
              <div key={idx} className="space-y-2">
                <label className="font-medium text-forest">{question}</label>
                <Textarea
                  placeholder="Your reflection..."
                  value={day30Responses[`q${idx}`] || ''}
                  onChange={(e) => setDay30Responses(prev => ({
                    ...prev,
                    [`q${idx}`]: e.target.value
                  }))}
                  className="min-h-[80px]"
                />
              </div>
            ))}
          </div>

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowDay30Dialog(false)}>
              Save for Later
            </Button>
            <Button 
              onClick={handleDay30Submit}
              disabled={completing || Object.keys(day30Responses).length < (day_30_checkin.questions?.length || 4)}
              className="btn-primary"
            >
              {completing ? 'Completing...' : 'Complete & Unlock 90-Day Plan'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Week Completion Celebration Dialog */}
      <Dialog open={!!weekCompletionDialog} onOpenChange={() => setWeekCompletionDialog(null)}>
        <DialogContent className="sm:max-w-lg">
          {weekCompletionDialog && WEEK_COMPLETION_MESSAGES[weekCompletionDialog] && (
            <>
              <DialogHeader className="text-center">
                <div 
                  className="mx-auto h-20 w-20 rounded-full flex items-center justify-center mb-4"
                  style={{ backgroundColor: WEEK_COMPLETION_MESSAGES[weekCompletionDialog].color + '20' }}
                >
                  <div 
                    className="h-14 w-14 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: WEEK_COMPLETION_MESSAGES[weekCompletionDialog].color }}
                  >
                    {weekCompletionDialog === 'week_4' ? (
                      <Trophy className="h-7 w-7 text-white" />
                    ) : (
                      <Check className="h-7 w-7 text-white" />
                    )}
                  </div>
                </div>
                <DialogTitle className="font-heading text-2xl text-forest">
                  {getRandomVariation(WEEK_COMPLETION_MESSAGES[weekCompletionDialog].titleVariants)}
                </DialogTitle>
                <Badge 
                  className="mx-auto mt-2 text-white"
                  style={{ backgroundColor: WEEK_COMPLETION_MESSAGES[weekCompletionDialog].color }}
                >
                  {WEEK_COMPLETION_MESSAGES[weekCompletionDialog].subtitle}
                </Badge>
              </DialogHeader>

              <div className="py-4 space-y-5">
                {/* Celebration Message */}
                <p className="text-gray-600 text-center">
                  {getRandomVariation(WEEK_COMPLETION_MESSAGES[weekCompletionDialog].messageVariants)}
                </p>

                {/* Journal Prompt Card */}
                <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-5 border border-amber-200">
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                      <BookOpen className="h-5 w-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-medium text-amber-800 text-sm uppercase tracking-wide mb-2">
                        Take a moment to journal
                      </p>
                      <p className="text-amber-900 italic">
                        "{getRandomVariation(WEEK_COMPLETION_MESSAGES[weekCompletionDialog].journalPrompts)}"
                      </p>
                    </div>
                  </div>
                </div>

                {/* What's Next Preview */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Star className="h-4 w-4 text-purple-500" />
                    <span className="text-sm font-medium text-gray-600">What's Next</span>
                  </div>
                  <p className="text-sm text-gray-500">
                    {WEEK_COMPLETION_MESSAGES[weekCompletionDialog].nextWeekPreview}
                  </p>
                </div>
              </div>

              <DialogFooter className="flex-col sm:flex-row gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => setWeekCompletionDialog(null)}
                  className="w-full sm:w-auto"
                >
                  Continue Here
                </Button>
                <Button 
                  onClick={() => {
                    setWeekCompletionDialog(null);
                    navigate('/journal');
                  }}
                  className="w-full sm:w-auto bg-amber-500 hover:bg-amber-600 text-white"
                >
                  <BookOpen className="h-4 w-4 mr-2" />
                  Open Soul Journal
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ActivationPathway;
